import { Fragment, useMemo, useState } from 'react';

import { useEffectiveUser } from '../../auth/useAuthUser';
import {
  useCloseCycle,
  useCoverageBootstrap,
  useMyPreferences,
  useOpenCycle,
  usePreferenceMatrix,
  useRunOptimizer,
  useSubmitPreferences,
  useWithdrawPreferences,
} from '../../hooks/useSectorCoverage';
import type {
  SectorCoverageOptimizeResponse,
  SectorCoverageSector,
  SectorCoverageUserSubmission,
} from '../../types';
import styles from './SectorCoveragePage.module.css';

type SubTab = 'my' | 'senior';

const NUM_SLOTS = 5;
const ACTIVE_REQUIRED = 2;
const NON_ACTIVE_REQUIRED = 2;

function fmtCycleLabel(label: string, openedAt: string | null): string {
  if (!openedAt) return label;
  const d = new Date(openedAt);
  if (isNaN(d.getTime())) return label;
  return `${label} · opened ${d.toLocaleDateString()}`;
}

export function SectorCoveragePage() {
  const auth = useEffectiveUser();
  const bootstrap = useCoverageBootstrap();
  const isSenior = bootstrap.data?.is_senior ?? false;
  const isAdmin = bootstrap.data?.is_admin ?? false;
  const isEligible = bootstrap.data?.is_eligible ?? false;
  const cycle = bootstrap.data?.cycle ?? null;

  // null = no explicit choice yet; default below depends on role.
  const [subTab, setSubTab] = useState<SubTab | null>(null);
  const effectiveSubTab: SubTab = subTab ?? (isSenior && !isEligible ? 'senior' : 'my');

  if (bootstrap.isLoading) {
    return <div className={styles.empty}>Loading sector coverage…</div>;
  }
  if (bootstrap.isError || !bootstrap.data) {
    return (
      <div className={styles.empty}>
        Failed to load sector coverage: {(bootstrap.error as Error)?.message ?? 'unknown error'}
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Sector Coverage</h1>
          {cycle ? (
            <div style={{ marginTop: 6 }}>
              <span className={styles.cycleBadge}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#2E7D32' }} />
                {fmtCycleLabel(cycle.label, cycle.opened_at)}
              </span>
            </div>
          ) : (
            <div style={{ marginTop: 6 }}>
              <span className={`${styles.cycleBadge} ${styles.cycleBadgeClosed}`}>
                No open cycle
              </span>
            </div>
          )}
        </div>
        {auth.effectiveEmail && (
          <div style={{ fontSize: 12, color: '#888' }}>
            Signed in as {auth.effectiveName || auth.effectiveEmail}
          </div>
        )}
      </div>

      {isSenior && (
        <div className={styles.subnav}>
          <button
            className={`${styles.subnavBtn} ${effectiveSubTab === 'my' ? styles.subnavBtnActive : ''}`}
            onClick={() => setSubTab('my')}
          >
            My Preferences
          </button>
          <button
            className={`${styles.subnavBtn} ${effectiveSubTab === 'senior' ? styles.subnavBtnActive : ''}`}
            onClick={() => setSubTab('senior')}
          >
            Senior Team View
          </button>
        </div>
      )}

      {effectiveSubTab === 'my' && (
        <MyPreferencesPanel
          isEligible={isEligible}
          hasOpenCycle={!!cycle}
          sectors={bootstrap.data.sectors}
        />
      )}

      {effectiveSubTab === 'senior' && isSenior && (
        <SeniorTeamPanel
          isAdmin={isAdmin}
          hasOpenCycle={!!cycle}
        />
      )}
    </div>
  );
}

// ── My Preferences panel ─────────────────────────────────────────────────

interface MyPreferencesPanelProps {
  isEligible: boolean;
  hasOpenCycle: boolean;
  sectors: SectorCoverageSector[];
}

function MyPreferencesPanel({ isEligible, hasOpenCycle, sectors }: MyPreferencesPanelProps) {
  const myPrefs = useMyPreferences(hasOpenCycle);

  if (!hasOpenCycle) {
    return (
      <div className={styles.noEligible}>
        No active sector-coverage cycle. An admin needs to open one.
      </div>
    );
  }
  if (!isEligible) {
    return (
      <div className={styles.noEligible}>
        Sector coverage preferences are collected from Associates, Senior Associates, and VPs.
        You'll see the results from the Senior Team view if you have access.
      </div>
    );
  }
  if (myPrefs.isLoading) {
    return <div className={styles.empty}>Loading your preferences…</div>;
  }

  // Remount the form on each new server payload so the hydrated initial
  // state stays simple (no useEffect/setState dance). Key changes whenever
  // submitted_at or cycle_id changes.
  const formKey = `${myPrefs.data?.cycle_id ?? 'none'}:${myPrefs.data?.submitted_at ?? 'empty'}`;
  return (
    <MyPreferencesForm
      key={formKey}
      sectors={sectors}
      initialPicks={myPrefs.data?.picks ?? []}
      initialWriteIn={myPrefs.data?.write_in ?? ''}
      hasExistingSubmission={(myPrefs.data?.picks.length ?? 0) > 0}
    />
  );
}

interface MyPreferencesFormProps {
  sectors: SectorCoverageSector[];
  initialPicks: { sector_id: number; rank: number }[];
  initialWriteIn: string;
  hasExistingSubmission: boolean;
}

function MyPreferencesForm({
  sectors,
  initialPicks,
  initialWriteIn,
  hasExistingSubmission,
}: MyPreferencesFormProps) {
  const submit = useSubmitPreferences();
  const withdraw = useWithdrawPreferences();

  const [slots, setSlots] = useState<(number | null)[]>(() => {
    const next: (number | null)[] = Array(NUM_SLOTS).fill(null);
    for (const p of initialPicks) {
      if (p.rank >= 1 && p.rank <= NUM_SLOTS) next[p.rank - 1] = p.sector_id;
    }
    return next;
  });
  const [writeIn, setWriteIn] = useState(initialWriteIn);
  const [editing, setEditing] = useState(!hasExistingSubmission);
  const [expandedSectorId, setExpandedSectorId] = useState<number | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const sectorById = useMemo(() => {
    const map = new Map<number, SectorCoverageSector>();
    for (const s of sectors) map.set(s.id, s);
    return map;
  }, [sectors]);

  const slotKinds = slots.map((sid) => (sid != null ? sectorById.get(sid)?.kind : null));
  const activeCount = slotKinds.filter((k) => k === 'active').length;
  const nonActiveCount = slotKinds.filter((k) => k === 'non_active').length;
  const filledCount = slots.filter((s) => s != null).length;

  const constraints = {
    five: filledCount === NUM_SLOTS,
    active: activeCount >= ACTIVE_REQUIRED,
    nonActive: nonActiveCount >= NON_ACTIVE_REQUIRED,
  };
  const canSubmit = constraints.five && constraints.active && constraints.nonActive;

  const addSector = (sectorId: number) => {
    if (slots.includes(sectorId)) return;
    const firstFree = slots.findIndex((s) => s == null);
    if (firstFree === -1) return;
    const next = [...slots];
    next[firstFree] = sectorId;
    setSlots(next);
  };

  const removeSlot = (idx: number) => {
    // Pull non-null entries to the front, padding with nulls.
    const compacted: (number | null)[] = slots.filter((s, i) => s != null && i !== idx);
    while (compacted.length < NUM_SLOTS) compacted.push(null);
    setSlots(compacted);
  };

  const moveSlot = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= NUM_SLOTS) return;
    const next = [...slots];
    [next[idx], next[target]] = [next[target], next[idx]];
    setSlots(next);
  };

  const onSubmit = () => {
    const picks = slots
      .map((sid, i) => (sid != null ? { sector_id: sid, rank: i + 1 } : null))
      .filter((p): p is { sector_id: number; rank: number } => p != null);
    submit.mutate(
      { picks, write_in: writeIn.trim() || null },
      {
        onSuccess: () => {
          setShowSuccess(true);
          setEditing(false);
          setTimeout(() => setShowSuccess(false), 4000);
        },
      },
    );
  };

  const onWithdraw = () => {
    withdraw.mutate(undefined, {
      onSuccess: () => {
        setSlots(Array(NUM_SLOTS).fill(null));
        setWriteIn('');
        setEditing(true);
      },
    });
  };

  // Submitted + not editing: read-only summary.
  if (!editing && filledCount === NUM_SLOTS) {
    return (
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <span>Your Submission</span>
          <button className={styles.btn} onClick={() => setEditing(true)}>
            Edit
          </button>
        </div>
        {showSuccess && <div className={styles.success}>Preferences saved.</div>}
        <div className={styles.rankSlots}>
          {slots.map((sid, i) => {
            const sector = sid != null ? sectorById.get(sid) : null;
            return (
              <div key={i} className={`${styles.rankSlot} ${styles.rankSlotFilled}`} style={{ cursor: 'default' }}>
                <span className={styles.rankNum}>{i + 1}</span>
                <span className={styles.rankSectorName}>
                  {sector?.name ?? '—'}{' '}
                  {sector && (
                    <span
                      className={`${styles.kindBadge} ${
                        sector.kind === 'active' ? styles.kindActive : styles.kindNonActive
                      }`}
                    >
                      {sector.kind === 'active' ? 'Active' : 'Non-active'}
                    </span>
                  )}
                </span>
                <span />
                <span />
              </div>
            );
          })}
        </div>
        {writeIn && (
          <div className={styles.writeIn}>
            <label className={styles.writeInLabel}>Write-in suggestion</label>
            <div style={{ fontSize: 13, color: '#1A1A1A' }}>{writeIn}</div>
          </div>
        )}
        <div className={styles.actions}>
          <button
            className={`${styles.btn} ${styles.btnDanger}`}
            onClick={onWithdraw}
            disabled={withdraw.isPending}
          >
            {withdraw.isPending ? 'Clearing…' : 'Clear Submissions'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.guidance}>
        <strong>Pick 5 sectors and stack-rank them by interest.</strong> At least{' '}
        <strong>2 must be active</strong> and at least <strong>2 must be non-active</strong>{' '}
        (monitoring, emerging, or on-hold). Closed sectors are excluded. Use the optional
        write-in below to suggest a sector that isn't on the list.
      </div>

      {showSuccess && <div className={styles.success}>Preferences saved.</div>}

      {submit.isError && (
        <div className={styles.error}>
          {(submit.error as { detail?: { detail?: string } })?.detail?.detail ??
            (submit.error as Error)?.message ??
            'Failed to save preferences.'}
        </div>
      )}

      <div className={styles.layout}>
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <span>Sectors ({sectors.length})</span>
            <span style={{ fontSize: 11, color: '#888', textTransform: 'none', fontWeight: 400 }}>
              click to add
            </span>
          </div>
          <div className={styles.sectorList}>
            {sectors.map((s) => {
              const added = slots.includes(s.id);
              const expanded = expandedSectorId === s.id;
              return (
                <div
                  key={s.id}
                  className={`${styles.sectorRow} ${added ? styles.sectorRowAdded : ''}`}
                  onClick={() => !added && addSector(s.id)}
                >
                  <span className={styles.sectorName}>
                    {s.name}
                    <span
                      className={`${styles.kindBadge} ${
                        s.kind === 'active' ? styles.kindActive : styles.kindNonActive
                      }`}
                    >
                      {s.stage}
                    </span>
                  </span>
                  <button
                    className={styles.sectorDescBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedSectorId(expanded ? null : s.id);
                    }}
                  >
                    {expanded ? 'hide' : 'info'}
                  </button>
                  <button
                    className={`${styles.addBtn} ${added ? styles.addBtnDisabled : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!added) addSector(s.id);
                    }}
                    disabled={added}
                  >
                    {added ? '✓' : '+ Add'}
                  </button>
                  {expanded && s.description && (
                    <div className={styles.sectorDesc}>{s.description}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <span>Your Ranked Picks</span>
          </div>
          <div className={styles.rankSlots}>
            {slots.map((sid, i) => {
              const sector = sid != null ? sectorById.get(sid) : null;
              return (
                <div
                  key={i}
                  className={`${styles.rankSlot} ${sector ? styles.rankSlotFilled : ''}`}
                  style={{ cursor: 'default' }}
                >
                  <span className={styles.rankNum}>{i + 1}</span>
                  <span className={sector ? styles.rankSectorName : styles.rankPlaceholder}>
                    {sector ? (
                      <>
                        {sector.name}{' '}
                        <span
                          className={`${styles.kindBadge} ${
                            sector.kind === 'active' ? styles.kindActive : styles.kindNonActive
                          }`}
                        >
                          {sector.kind === 'active' ? 'Active' : 'Non-active'}
                        </span>
                      </>
                    ) : (
                      'click a sector on the left'
                    )}
                  </span>
                  {sector ? (
                    <span style={{ display: 'flex', gap: 4 }}>
                      <button
                        className={styles.rankRemove}
                        onClick={() => moveSlot(i, -1)}
                        disabled={i === 0}
                        style={{ color: i === 0 ? '#E5E5E0' : undefined }}
                        title="Move up"
                      >
                        ▲
                      </button>
                      <button
                        className={styles.rankRemove}
                        onClick={() => moveSlot(i, 1)}
                        disabled={i === NUM_SLOTS - 1 || slots[i + 1] == null}
                        style={{
                          color:
                            i === NUM_SLOTS - 1 || slots[i + 1] == null ? '#E5E5E0' : undefined,
                        }}
                        title="Move down"
                      >
                        ▼
                      </button>
                    </span>
                  ) : (
                    <span />
                  )}
                  {sector ? (
                    <button
                      className={styles.rankRemove}
                      onClick={() => removeSlot(i)}
                      title="Remove"
                    >
                      ✕
                    </button>
                  ) : (
                    <span />
                  )}
                </div>
              );
            })}
          </div>

          <div className={styles.writeIn}>
            <label className={styles.writeInLabel}>Suggest a sector (optional)</label>
            <input
              className={styles.writeInInput}
              type="text"
              maxLength={500}
              placeholder="e.g. Carbon capture services"
              value={writeIn}
              onChange={(e) => setWriteIn(e.target.value)}
            />
            <div className={styles.writeInHelp}>
              One free-text suggestion per cycle. Visible to the senior team only.
            </div>
          </div>

          <div className={styles.counters}>
            <span className={`${styles.counter} ${constraints.active ? styles.counterOk : styles.counterFail}`}>
              Active {activeCount}/{ACTIVE_REQUIRED}{' '}
              {constraints.active ? '✓' : ''}
            </span>
            <span className={`${styles.counter} ${constraints.nonActive ? styles.counterOk : styles.counterFail}`}>
              Non-active {nonActiveCount}/{NON_ACTIVE_REQUIRED}{' '}
              {constraints.nonActive ? '✓' : ''}
            </span>
            <span className={`${styles.counter} ${constraints.five ? styles.counterOk : styles.counterFail}`}>
              Total {filledCount}/{NUM_SLOTS} {constraints.five ? '✓' : ''}
            </span>
          </div>

          <div className={styles.actions}>
            {hasExistingSubmission && (
              <button className={styles.btn} onClick={() => setEditing(false)}>
                Cancel
              </button>
            )}
            <button
              className={styles.btnPrimary}
              onClick={onSubmit}
              disabled={!canSubmit || submit.isPending}
            >
              {submit.isPending
                ? 'Saving…'
                : hasExistingSubmission
                ? 'Save changes'
                : 'Submit preferences'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Senior Team panel ────────────────────────────────────────────────────

interface SeniorTeamPanelProps {
  isAdmin: boolean;
  hasOpenCycle: boolean;
}

function SeniorTeamPanel({ isAdmin, hasOpenCycle }: SeniorTeamPanelProps) {
  const matrix = usePreferenceMatrix(true);
  const runOptimizer = useRunOptimizer();
  const openCycle = useOpenCycle();
  const closeCycle = useCloseCycle();

  const [maxPerUser, setMaxPerUser] = useState<number | null>(null);
  const [optResult, setOptResult] = useState<SectorCoverageOptimizeResponse | null>(null);
  const [newCycleLabel, setNewCycleLabel] = useState('');
  const [newCycleNotes, setNewCycleNotes] = useState('');

  if (matrix.isLoading) return <div className={styles.empty}>Loading senior view…</div>;
  if (matrix.isError || !matrix.data) {
    const err = matrix.error as { message?: string; detail?: unknown } | null;
    const detail = err?.detail;
    const detailStr =
      typeof detail === 'string'
        ? detail
        : detail && typeof detail === 'object' && 'detail' in (detail as object)
        ? String((detail as { detail: unknown }).detail)
        : detail
        ? JSON.stringify(detail)
        : null;
    return (
      <div className={styles.empty}>
        <div>Failed to load senior view: {err?.message ?? 'unknown error'}</div>
        {detailStr && (
          <div style={{ marginTop: 8, fontSize: 11, color: '#888', fontFamily: "'DM Mono', monospace" }}>
            {detailStr}
          </div>
        )}
      </div>
    );
  }

  const { eligible_users, submissions, sectors, cycle } = matrix.data;
  const submittedEmails = new Set(submissions.map((s) => s.user_email));

  const handleRunOptimizer = () => {
    runOptimizer.mutate(
      { maxPerUser },
      { onSuccess: (data) => setOptResult(data) },
    );
  };

  const handleOpenCycle = () => {
    if (!newCycleLabel.trim()) return;
    openCycle.mutate(
      { label: newCycleLabel.trim(), notes: newCycleNotes.trim() || null },
      {
        onSuccess: () => {
          setNewCycleLabel('');
          setNewCycleNotes('');
        },
      },
    );
  };

  return (
    <div className={styles.senior}>
      {isAdmin && (
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <span>Cycle Controls (admin)</span>
          </div>
          <div className={styles.cycleControls}>
            {cycle ? (
              <div className={styles.cycleControlsRow}>
                <span className={styles.statusBanner}>
                  Open cycle: <strong style={{ color: '#1A1A1A' }}>{cycle.label}</strong>
                </span>
                <button
                  className={`${styles.btn} ${styles.btnDanger}`}
                  onClick={() => closeCycle.mutate(cycle.id)}
                  disabled={closeCycle.isPending}
                >
                  {closeCycle.isPending ? 'Closing…' : 'Close cycle'}
                </button>
              </div>
            ) : (
              <span className={styles.statusBanner}>No open cycle.</span>
            )}
            <div className={styles.cycleControlsRow}>
              <input
                className={styles.cycleInput}
                placeholder="New cycle label, e.g. 2026-Q2"
                value={newCycleLabel}
                onChange={(e) => setNewCycleLabel(e.target.value)}
              />
              <input
                className={styles.cycleInput}
                placeholder="Notes (optional)"
                value={newCycleNotes}
                onChange={(e) => setNewCycleNotes(e.target.value)}
              />
              <button
                className={styles.btnPrimary}
                onClick={handleOpenCycle}
                disabled={openCycle.isPending || !newCycleLabel.trim()}
              >
                {openCycle.isPending ? 'Opening…' : cycle ? 'Replace cycle' : 'Open new cycle'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <span>Submission Status ({submissions.length}/{eligible_users.length})</span>
        </div>
        <div className={styles.statusGrid}>
          {eligible_users.map((email) => {
            const submitted = submittedEmails.has(email);
            const sub = submissions.find((s) => s.user_email === email);
            return (
              <div
                key={email}
                className={`${styles.statusCell} ${submitted ? styles.statusCellSubmitted : ''}`}
              >
                <span
                  className={`${styles.statusDot} ${submitted ? styles.statusDotSubmitted : ''}`}
                />
                <span style={{ fontWeight: 500 }}>{sub?.user_name ?? email}</span>
                {sub?.title && <span style={{ color: '#888', fontSize: 11 }}>{sub.title}</span>}
              </div>
            );
          })}
        </div>
      </div>

      {submissions.length > 0 && (
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <span>Preferences Matrix</span>
            <span style={{ fontSize: 11, color: '#888', textTransform: 'none', fontWeight: 400 }}>
              cells are rank #
            </span>
          </div>
          <PreferencesMatrix
            sectors={sectors}
            submissions={submissions}
          />
        </div>
      )}

      {submissions.some((s) => !!s.write_in) && (
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <span>Write-in Suggestions</span>
          </div>
          <div className={styles.writeInList}>
            {submissions
              .filter((s) => !!s.write_in)
              .map((s) => (
                <div key={s.user_email} className={styles.writeInRow}>
                  <span className={styles.writeInRowName}>{s.user_name ?? s.user_email}</span>
                  <span className={styles.writeInRowSugg}>{s.write_in}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <span>Optimizer</span>
        </div>
        <div className={styles.optimizerControls}>
          <label style={{ fontSize: 12, color: '#555' }}>
            Max sectors per deal team member:{' '}
            <input
              type="number"
              min={1}
              max={10}
              value={maxPerUser ?? ''}
              placeholder="auto"
              onChange={(e) =>
                setMaxPerUser(e.target.value === '' ? null : parseInt(e.target.value, 10))
              }
              className={styles.cycleInput}
              style={{ minWidth: 80, width: 80 }}
            />
          </label>
          <button
            className={styles.btnPrimary}
            onClick={handleRunOptimizer}
            disabled={runOptimizer.isPending || !hasOpenCycle || submissions.length === 0}
          >
            {runOptimizer.isPending ? 'Running…' : 'Run optimizer'}
          </button>
          {optResult && (
            <span className={styles.statusBanner}>
              Score: {optResult.total_score} / {optResult.max_possible_score} ·{' '}
              status: {optResult.status} · {optResult.config.submitter_count ?? 0} submitter
              {(optResult.config.submitter_count ?? 0) === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {optResult && optResult.warnings.length > 0 && (
          <div className={styles.warningBanner}>
            {optResult.warnings.map((w, i) => (
              <div key={i}>⚠ {w}</div>
            ))}
          </div>
        )}
        {optResult && (
          <>
            <div className={styles.assignmentList}>
              {optResult.assignments.map((a) => (
                <div key={a.user_email} className={styles.assignmentCard}>
                  <div className={styles.assignmentHeader}>
                    <span className={styles.assignmentName}>{a.user_name ?? a.user_email}</span>
                    <span className={styles.assignmentPoints}>{a.total_points} pts</span>
                  </div>
                  {a.sectors.length === 0 ? (
                    <div className={styles.assignmentSector} style={{ color: '#888', fontStyle: 'italic' }}>
                      No sectors assigned
                    </div>
                  ) : (
                    a.sectors.map((s) => (
                      <div key={s.sector_id} className={styles.assignmentSector}>
                        <span>
                          {s.name}{' '}
                          <span
                            className={`${styles.kindBadge} ${
                              s.kind === 'active' ? styles.kindActive : styles.kindNonActive
                            }`}
                          >
                            {s.stage}
                          </span>
                        </span>
                        <span className={styles.assignmentSectorRank}>
                          {s.rank_chosen ? `#${s.rank_chosen} · ${s.points} pts` : 'unranked'}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              ))}
            </div>
            {optResult.sectors_uncovered.length > 0 && (
              <div className={styles.warningBanner} style={{ background: '#F8F8F6', borderColor: '#E5E5E0', color: '#666' }}>
                <strong>Uncovered sectors:</strong>{' '}
                {optResult.sectors_uncovered.map((s) => s.name).join(', ')}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface PreferencesMatrixProps {
  sectors: SectorCoverageSector[];
  submissions: SectorCoverageUserSubmission[];
}

type MatrixSortKey = 'sector' | 'sponsor';
type MatrixSortDir = 'asc' | 'desc';

const SPONSOR_UNASSIGNED_KEY = '__unassigned__';

function rankCellClass(rank: number): string {
  if (rank === 1) return styles.rankCell1;
  if (rank === 2) return styles.rankCell2;
  if (rank === 3) return styles.rankCell3;
  if (rank === 4) return styles.rankCell4;
  return styles.rankCell5;
}

function PreferencesMatrix({ sectors, submissions }: PreferencesMatrixProps) {
  // Only render rows for sectors that received at least one pick or are
  // active — otherwise the matrix is mostly empty noise.
  const sectorIdsWithPicks = new Set<number>();
  for (const sub of submissions) {
    for (const p of sub.picks) sectorIdsWithPicks.add(p.sector_id);
  }
  const visibleSectors = sectors.filter(
    (s) => sectorIdsWithPicks.has(s.id) || s.kind === 'active',
  );

  const [sortKey, setSortKey] = useState<MatrixSortKey>('sector');
  const [sortDir, setSortDir] = useState<MatrixSortDir>('asc');
  const [groupBySponsor, setGroupBySponsor] = useState(false);

  const sponsorKey = (s: SectorCoverageSector) =>
    s.sponsor_name?.toLowerCase() ?? SPONSOR_UNASSIGNED_KEY;

  const sortedSectors = useMemo(() => {
    const out = [...visibleSectors];
    out.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'sector') {
        cmp = a.name.localeCompare(b.name);
      } else {
        const av = sponsorKey(a);
        const bv = sponsorKey(b);
        // Push unassigned to the end on asc, top on desc.
        if (av === SPONSOR_UNASSIGNED_KEY && bv !== SPONSOR_UNASSIGNED_KEY) cmp = 1;
        else if (bv === SPONSOR_UNASSIGNED_KEY && av !== SPONSOR_UNASSIGNED_KEY) cmp = -1;
        else cmp = av.localeCompare(bv);
        if (cmp === 0) cmp = a.name.localeCompare(b.name);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return out;
  }, [visibleSectors, sortKey, sortDir]);

  const grouped = useMemo(() => {
    if (!groupBySponsor) return null;
    const map = new Map<string, { label: string; sectors: SectorCoverageSector[] }>();
    for (const s of sortedSectors) {
      const key = sponsorKey(s);
      const label = s.sponsor_name ?? 'No sponsor';
      if (!map.has(key)) map.set(key, { label, sectors: [] });
      map.get(key)!.sectors.push(s);
    }
    // Sort group keys: real names first (alphabetical), unassigned last.
    return [...map.entries()].sort(([ak], [bk]) => {
      if (ak === SPONSOR_UNASSIGNED_KEY) return 1;
      if (bk === SPONSOR_UNASSIGNED_KEY) return -1;
      return ak.localeCompare(bk);
    });
  }, [sortedSectors, groupBySponsor]);

  function toggleSort(key: MatrixSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function arrow(key: MatrixSortKey) {
    if (sortKey !== key) return <span className={`${styles.sortArrow} ${styles.sortArrowInactive}`}>▾</span>;
    return <span className={styles.sortArrow}>{sortDir === 'asc' ? '▲' : '▼'}</span>;
  }

  const totalCols = 2 + submissions.length;

  function renderSectorRow(s: SectorCoverageSector) {
    return (
      <tr key={s.id}>
        <td className={styles.sectorCell}>
          {s.name}{' '}
          <span
            className={`${styles.kindBadge} ${
              s.kind === 'active' ? styles.kindActive : styles.kindNonActive
            }`}
          >
            {s.stage}
          </span>
        </td>
        <td className={styles.sponsorCell}>
          {s.sponsor_name ?? <span className={styles.sponsorMissing}>none</span>}
        </td>
        {submissions.map((sub) => {
          const pick = sub.picks.find((p) => p.sector_id === s.id);
          return (
            <td key={sub.user_email} className={pick ? rankCellClass(pick.rank) : ''}>
              {pick ? `#${pick.rank}` : ''}
            </td>
          );
        })}
      </tr>
    );
  }

  return (
    <>
      <div className={styles.matrixToolbar}>
        <label>
          Group by:{' '}
          <select
            value={groupBySponsor ? 'sponsor' : 'none'}
            onChange={(e) => setGroupBySponsor(e.target.value === 'sponsor')}
          >
            <option value="none">None</option>
            <option value="sponsor">Sponsor</option>
          </select>
        </label>
      </div>
      <div className={styles.matrixWrap}>
        <table className={styles.matrix}>
          <thead>
            <tr>
              <th
                className={`${styles.sectorHeader} ${styles.sortable}`}
                onClick={() => toggleSort('sector')}
              >
                Sector{arrow('sector')}
              </th>
              <th
                className={styles.sortable}
                onClick={() => toggleSort('sponsor')}
              >
                Sponsor{arrow('sponsor')}
              </th>
              {submissions.map((sub) => (
                <th key={sub.user_email} title={sub.user_email}>
                  {sub.user_name?.split(' ')[0] ?? sub.user_email}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grouped
              ? grouped.map(([key, group]) => (
                  <Fragment key={key}>
                    <tr className={styles.groupHeaderRow}>
                      <td colSpan={totalCols}>
                        {group.label} ({group.sectors.length})
                      </td>
                    </tr>
                    {group.sectors.map(renderSectorRow)}
                  </Fragment>
                ))
              : sortedSectors.map(renderSectorRow)}
          </tbody>
        </table>
      </div>
    </>
  );
}
